import React, { useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  CardPaymentSelectors,
  cartQuoteSelectors,
  getNestedChildState,
  PaymentInformationChildState,
  RewardsAndPayment,
  RewardsPaymentEventTypes,
  RewardsPaymentSelectors,
  useCheckoutState,
  useCheckoutStateSelector,
  WalletEventTypes,
  WalletSelectors,
} from "@capone/checkout";
import {
  ActionButton,
  B2BSpinner,
  BackButton,
  GenericInfoPopup,
  Icon,
  IconName,
  LoadingPopup,
  MobileFloatingButton,
  MobilePopoverCard,
  PoweredByHopper,
  TEST_CARD_LAST_FOURS,
  useDeviceTypes,
  Tooltip,
} from "halifax";
import {
  CorpSessionInfo,
  Funnel,
  TokenizeCardErrors,
  TravelProductEnum,
  TravelWalletCredit,
  TravelWalletOffer,
  VIEWED_PACKAGE_CHECKOUT_PAYMENTS,
} from "redmond";
import clsx from "clsx";
import { Box, Typography } from "@material-ui/core";
import {
  DO_NOT_APPLY_REWARDS_KEY,
  getConfigTenant,
  getCurrentCorpAccount,
  isCorpTenant,
  isVesperTenant,
  useIsTravelApp,
} from "@capone/common";
import { useExperimentIsVariant } from "@capone/experiments";
import { PackagesRewardsAndPaymentConnectorProps } from "./container";
import { config } from "../../../../api/config";
import { PackagesMachineContext } from "../../state/types";
import { Event, TEvent } from "../../state/events";
import { getErrorModalProps } from "./utils";
import {
  CARD_VERIFY_TEXT,
  MOBILE_HEADING_SUBTITLE_TEXT,
  MOBILE_HEADING_TITLE_TEXT,
  NO_SELECTED_CARD_TEXT,
  NO_SELECTED_REWARDS_TEXT,
  PAYMENT_EARN_INFO_TOOLTIP_TEXT,
  PAYMENT_MOBILE_TITLE,
  REVIEW_PACKAGE_CTA_TEXT,
  REVIEW_TRIP_CTA_TEXT,
} from "./textConstants";
import "./styles.scss";
import { ClientContext } from "../../../../App";
import { CONFIRM_AND_BOOK } from "../textConstants";
import { PackagesPriceBreakdown } from "../PriceBreakdown";
import { DesktopPackageBookValidationError } from "../DesktopPackagesBookWorkflow";
import {
  AVAILABLE,
  CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
} from "../../../../context/experiments";
import { getPackageHotelPayNowTotal } from "../../state/selectors/common";
import { MobileBookingSteps } from "../MobileBookingSteps";
import { trackEvent } from "../../../../api/v0/trackEvent";
import dayjs from "dayjs";

export interface IPackagesRewardsAndPaymentProps
  extends RouteComponentProps,
    PackagesRewardsAndPaymentConnectorProps {
  isMobile?: boolean;
  validationErrorTypes?: DesktopPackageBookValidationError[];
  setValidationErrorTypes?: (
    types: DesktopPackageBookValidationError[]
  ) => void;
}

export const PackagesRewardsAndPayment = ({
  rewardsAccounts,
  isMobile,
  validationErrorTypes,
  setValidationErrorTypes,
  allWalletOffers,
  fetchTravelWalletDetails,
}: IPackagesRewardsAndPaymentProps) => {
  const { sessionInfo, isRewardsDisabled } = React.useContext(ClientContext);

  const { isTravelApp } = useIsTravelApp();

  const { matchesMobile } = useDeviceTypes();

  const [tokenizeErrors, setTokenizeErrors] = React.useState<
    TokenizeCardErrors[]
  >([]);
  const [invalidCapOneCardError, setInvalidCapOneCardError] =
    React.useState(false);

  const expState = useExperiments();

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const isFtbOfferAdditionalPlacementsEnabled =
    getExperimentVariant(
      expState.experiments,
      CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS
    ) === AVAILABLE;

  const packageHotelPayNowTotal = useCheckoutStateSelector(
    getPackageHotelPayNowTotal
  );

  const [state, send] = useCheckoutState<TEvent, PackagesMachineContext>();

  const nestedChildState = getNestedChildState(state.value);

  const allowAnyCards = useExperimentIsVariant(
    "c1-corporate-credit-card-validation",
    "any-spark"
  );

  const isNonFinancialUser =
    (sessionInfo as CorpSessionInfo)?.corporateInfo?.cap1Role === "NFU";

  /* selectors */

  const paymentMethods = useCheckoutStateSelector(
    CardPaymentSelectors.getPaymentMethods
  );

  const offers = useCheckoutStateSelector(WalletSelectors.getOffers);
  const credits = useCheckoutStateSelector(
    WalletSelectors.getApplicableCredits
  );
  const ineligibleCreditsBreakdown = useCheckoutStateSelector(
    WalletSelectors.getIneligibleCreditsBreakdown
  );

  const offerToApply = useCheckoutStateSelector(
    WalletSelectors.getSelectedOffer
  );
  const creditsToApply = useCheckoutStateSelector(
    WalletSelectors.getTravelWalletCreditToApply
  );
  const isTravelCreditPaymentOnly = useCheckoutStateSelector(
    WalletSelectors.getIsTravelCreditPaymentOnly
  );
  const isTravelWalletOfferPaymentOnly = useCheckoutStateSelector(
    WalletSelectors.getIsTravelWalletOfferPaymentOnly
  );
  const isStackedTravelWalletPaymentOnly = useCheckoutStateSelector(
    WalletSelectors.getIsStackedTravelWalletPaymentOnly
  );
  const creditTransactionHistory = useCheckoutStateSelector(
    WalletSelectors.getCreditTransactionHistory
  );
  const maxApplicableTravelWalletCredit = useCheckoutStateSelector(
    WalletSelectors.getMaxApplicableCreditAmount
  );

  const paymentMethodsLoading = useCheckoutStateSelector(
    CardPaymentSelectors.getIsPaymentLoading
  );
  const selectedPaymentMethodId = useCheckoutStateSelector(
    CardPaymentSelectors.getSelectedPaymentMethodId
  );
  const isPaymentError = useCheckoutStateSelector(
    CardPaymentSelectors.getIsPaymentError
  );
  const paymentError = useCheckoutStateSelector(
    CardPaymentSelectors.getPaymentError
  );

  const addPaymentMethodModalOpen = useCheckoutStateSelector(
    CardPaymentSelectors.getOpenAddPaymentMethodModal
  );

  const selectedRewardsAccountId = useCheckoutStateSelector(
    RewardsPaymentSelectors.getSelectedAccountId
  );
  const cartBreakdownBalance = useCheckoutStateSelector(
    cartQuoteSelectors.getCartBreakdownBalance
  );
  const rewardsPaymentInFiatCurrency = useCheckoutStateSelector(
    RewardsPaymentSelectors.getRewardsFiatAmountToApply
  );
  const rewardsPaymentInRewardsCurrency = useCheckoutStateSelector(
    RewardsPaymentSelectors.getRewardsAmountToApply
  );
  const isCreditCardPaymentRequired = useCheckoutStateSelector(
    CardPaymentSelectors.getIsCreditCardPaymentRequired
  );
  const totalCardPaymentRequired = useCheckoutStateSelector(
    CardPaymentSelectors.getTotalCardPaymentRequired
  );

  const isCostCoveredBySelectedPaymentMethods = useCheckoutStateSelector(
    CardPaymentSelectors.getIsCostCoveredBySelectedPaymentMethods
  );
  const earnValueByRewardsAccountId = useCheckoutStateSelector(
    RewardsPaymentSelectors.getEarnValueByRewardsAccount
  );
  const rewardsPaymentAllowed = useCheckoutStateSelector(
    RewardsPaymentSelectors.getRewardsPaymentAllowed
  );
  const hasSomeRewardsBalance = useCheckoutStateSelector(
    RewardsPaymentSelectors.getHasSomeRewardsBalance
  );

  const isCorporate = isCorpTenant(getConfigTenant());
  const isVesper = isVesperTenant(getConfigTenant());

  const filteredRewardsAccounts = React.useMemo(() => {
    if (isCorporate && sessionInfo) {
      const currentAccount = getCurrentCorpAccount(
        rewardsAccounts,
        sessionInfo
      );
      return currentAccount ? [currentAccount] : rewardsAccounts;
    }
    return rewardsAccounts;
  }, [rewardsAccounts, sessionInfo, isCorporate]);

  /* action callbacks */

  const verifyPaymentMethod = (token: string) =>
    send({ type: Event.VERIFY_PAYMENT_METHOD, token });

  const deletePaymentMethod = (paymentId: string) =>
    send({ type: Event.DELETE_PAYMENT_METHOD, paymentMethod: { paymentId } });

  const setSelectedPaymentMethodId = (
    paymentMethodId: string,
    rewardsAccountId?: string
  ) => {
    send({
      type: Event.SET_SELECTED_PAYMENT_METHOD_ID,
      paymentMethod: { paymentId: paymentMethodId, rewardsAccountId },
    });
  };

  const handlePaymentMethodAdd = (token: string, last4: string) => {
    const account = filteredRewardsAccounts.find(
      (account) =>
        account.lastFour === last4 ||
        account.lastFourVirtualCardNumbers?.includes(last4)
    );
    let matchingRewardsAccount = account;

    const isTestCard =
      window.__mclean_env__.ENV !== "production" &&
      TEST_CARD_LAST_FOURS.includes(last4);
    if (isTestCard) {
      matchingRewardsAccount = filteredRewardsAccounts[0];
    }

    if (!!matchingRewardsAccount || isTestCard || isCorpTenant(config.TENANT)) {
      verifyPaymentMethod(token);
      setSelectedPaymentMethodId(token);
    } else {
      send(Event.CLOSE_PAYMENT_FORM);
      setInvalidCapOneCardError(true);
    }
  };

  const handleClearErrors = () => {
    send(Event.CLEAR_PAYMENT_ERROR);
    setTokenizeErrors([]);
    setInvalidCapOneCardError(false);
    send(Event.OPEN_PAYMENT_FORM);
  };

  const handleClickAddPaymentMethod = () => send(Event.OPEN_PAYMENT_FORM);

  const handleCloseAddPaymentMethod = () => send(Event.CLOSE_PAYMENT_FORM);

  const setSelectedRewardsAccountId = (accountId?: string) =>
    send({
      type: RewardsPaymentEventTypes.SET_SELECTED_REWARDS_ACCOUNT_ID,
      selectedAccountId: accountId,
    });

  const setRewardsPaymentAmount = (amount: number) =>
    send({
      type: RewardsPaymentEventTypes.SET_REWARDS_AMOUNT_TO_PAY,
      rewardsAmountToPay: amount,
    });

  const setOfferToApply = (offer?: TravelWalletOffer) =>
    offer
      ? send({ type: WalletEventTypes.SET_SELECTED_OFFER, offerId: offer.id })
      : send(WalletEventTypes.REMOVE_SELECTED_OFFER);

  const setCreditAmountToApply = (credits?: TravelWalletCredit) => {
    setSelectedRewardsAccountId();
    setRewardsPaymentAmount(0);
    send({
      type: WalletEventTypes.SET_CREDIT_AMOUNT_TO_APPLY,
      amount: credits?.amount?.amount || 0,
    });
  };

  const canContinue =
    isMobile ||
    (isCostCoveredBySelectedPaymentMethods && !!selectedRewardsAccountId);

  const handleContinue = () => {
    send({
      type: Event.SET_INITIAL_BOOK_TIME,
      initialTime: dayjs().toISOString(),
    });
    if (canContinue) {
      setValidationErrorTypes?.([]);
      send(Event.NEXT);
    } else {
      setValidationErrorTypes?.(
        (() => {
          switch (true) {
            case !selectedRewardsAccountId:
              return ["rewards"];
            case isCreditCardPaymentRequired && !selectedPaymentMethodId:
              return ["card"];
            default:
              return [];
          }
        })()
      );
    }
  };

  const handleBack = () => {
    send(Event.PREVIOUS);
  };

  const errorModalOpen =
    !!tokenizeErrors.length || invalidCapOneCardError || isPaymentError;

  const currentAccount = getCurrentCorpAccount(rewardsAccounts, sessionInfo);

  const errorModalProps = getErrorModalProps(
    tokenizeErrors,
    invalidCapOneCardError,
    handleClearErrors,
    paymentError,
    currentAccount || filteredRewardsAccounts[0],
    isCorpTenant(config.TENANT)
  );

  React.useEffect(() => {
    if (
      (validationErrorTypes?.includes("rewards") && selectedRewardsAccountId) ||
      (validationErrorTypes?.includes("card") &&
        isCreditCardPaymentRequired &&
        selectedPaymentMethodId)
    ) {
      setValidationErrorTypes?.([]);
    }
  }, [
    selectedRewardsAccountId,
    isCreditCardPaymentRequired,
    selectedPaymentMethodId,
    validationErrorTypes,
  ]);

  React.useEffect(() => {
    if (
      (isCostCoveredBySelectedPaymentMethods && !selectedRewardsAccountId) ||
      (isPackageCheckoutOptimizationExperiment && !hasSomeRewardsBalance)
    ) {
      setSelectedRewardsAccountId(DO_NOT_APPLY_REWARDS_KEY);
    }
  }, [
    isCostCoveredBySelectedPaymentMethods,
    selectedRewardsAccountId,
    hasSomeRewardsBalance,
  ]);

  React.useEffect(() => {
    trackEvent({
      eventName: VIEWED_PACKAGE_CHECKOUT_PAYMENTS,
      properties: {},
    });
  }, []);

  React.useEffect(() => {
    if (isFtbOfferAdditionalPlacementsEnabled && isCorporate) {
      fetchTravelWalletDetails();
    }
  }, []);

  const renderContent = useMemo(
    () => (
      <RewardsAndPayment
        rewardsAccounts={filteredRewardsAccounts}
        paymentMethods={paymentMethods}
        loadingPaymentMethods={paymentMethodsLoading}
        spreedlyEnvironmentKey={config.spreedlyEnvironmentKey}
        selectedPaymentMethodId={selectedPaymentMethodId}
        verifyingPaymentMethod={
          nestedChildState === PaymentInformationChildState.verify
        }
        isMobile={matchesMobile}
        onRemovePaymentMethod={deletePaymentMethod}
        onSelectPaymentMethod={setSelectedPaymentMethodId}
        onAddPaymentMethod={handlePaymentMethodAdd}
        onAddPaymentMethodError={setTokenizeErrors}
        onClickAddPaymentMethod={handleClickAddPaymentMethod}
        closePaymentModal={handleCloseAddPaymentMethod}
        addPaymentModalOpen={addPaymentMethodModalOpen}
        selectedRewardsAccountId={selectedRewardsAccountId}
        setSelectedRewardsAccountId={setSelectedRewardsAccountId}
        total={
          cartBreakdownBalance
            ? {
                ...cartBreakdownBalance,
                rewards: cartBreakdownBalance.accountSpecific,
              }
            : undefined
        }
        rewardsPaymentInFiatCurrency={rewardsPaymentInFiatCurrency}
        rewardsPaymentInRewardsCurrency={rewardsPaymentInRewardsCurrency}
        setRewardsPaymentAmount={setRewardsPaymentAmount}
        isCreditCardPaymentRequired={isCreditCardPaymentRequired}
        offers={offers}
        credits={credits}
        offerToApply={offerToApply}
        creditsToApply={creditsToApply}
        isTravelCreditPaymentOnly={isTravelCreditPaymentOnly}
        isTravelWalletOfferPaymentOnly={isTravelWalletOfferPaymentOnly}
        isStackedTravelWalletPaymentOnly={isStackedTravelWalletPaymentOnly}
        firstName={sessionInfo?.userInfo.firstName || ""}
        creditTransactionHistory={creditTransactionHistory}
        setOfferToApply={setOfferToApply}
        setCreditToApply={setCreditAmountToApply}
        maxApplicableTravelWalletCredit={maxApplicableTravelWalletCredit}
        cardPaymentDisabled={
          selectedRewardsAccountId ? !isCreditCardPaymentRequired : true
        }
        earnValueByRewardsAccountId={earnValueByRewardsAccountId}
        rewardsRedemptionIneligible={!rewardsPaymentAllowed}
        rewardsError={
          validationErrorTypes?.includes("rewards")
            ? NO_SELECTED_REWARDS_TEXT
            : undefined
        }
        cardError={
          validationErrorTypes?.includes("card")
            ? NO_SELECTED_CARD_TEXT
            : undefined
        }
        product={TravelProductEnum.Packages}
        cardPayment={{
          value: totalCardPaymentRequired,
          currencyCode: "USD",
          currencySymbol: "$",
        }}
        enableCheckoutCXEnhancementsV1={isPackageCheckoutOptimizationExperiment}
        corpSessionInfo={
          sessionInfo && "corporateInfo" in sessionInfo
            ? (sessionInfo as CorpSessionInfo)
            : undefined
        }
        allowAnyCards={allowAnyCards || isNonFinancialUser}
        ineligibleCreditsBreakdown={ineligibleCreditsBreakdown}
        allWalletOffers={
          isFtbOfferAdditionalPlacementsEnabled && isCorporate
            ? allWalletOffers
            : undefined
        }
        bookingTotal={
          isFtbOfferAdditionalPlacementsEnabled && isCorporate
            ? packageHotelPayNowTotal
            : undefined
        }
        currentFunnel={Funnel.Package}
        isHAModeRewardsDisabled={isRewardsDisabled}
      />
    ),
    [
      filteredRewardsAccounts,
      paymentMethods,
      config.spreedlyEnvironmentKey,
      selectedPaymentMethodId,
      nestedChildState,
      matchesMobile,
      addPaymentMethodModalOpen,
      selectedRewardsAccountId,
      cartBreakdownBalance,
      rewardsPaymentInFiatCurrency,
      rewardsPaymentInRewardsCurrency,
      isCreditCardPaymentRequired,
      offers,
      credits,
      offerToApply,
      creditsToApply,
      isTravelCreditPaymentOnly,
      isTravelWalletOfferPaymentOnly,
      isStackedTravelWalletPaymentOnly,
      sessionInfo,
      creditTransactionHistory,
      maxApplicableTravelWalletCredit,
      earnValueByRewardsAccountId,
      rewardsPaymentAllowed,
      validationErrorTypes,
      totalCardPaymentRequired,
      isPackageCheckoutOptimizationExperiment,
      allowAnyCards,
      isNonFinancialUser,
      isFtbOfferAdditionalPlacementsEnabled,
      isCorporate,
      allWalletOffers,
      packageHotelPayNowTotal,
      isRewardsDisabled,
    ]
  );

  return (
    <>
      {isMobile ? (
        <MobilePopoverCard
          open
          className={clsx("packages-book-mobile-payment-popover", {
            "cx-enhancements-v1": isPackageCheckoutOptimizationExperiment,
            redesigned: isVesper,
          })}
          fullScreen
          onClose={() => {}}
          topLeftButton={
            isTravelApp ? undefined : (
              <BackButton
                className="packages-book-mobile-payment-popover-back-button"
                onClick={handleBack}
              />
            )
          }
          headerElement={
            isPackageCheckoutOptimizationExperiment ? (
              <Typography className="header-text">
                {PAYMENT_MOBILE_TITLE}
              </Typography>
            ) : (
              <PackagesPriceBreakdown isMobile dropdown />
            )
          }
        >
          <Box className="packages-book-mobile-payment-header">
            {isPackageCheckoutOptimizationExperiment ? (
              <>
                <MobileBookingSteps currentStep={5} />
                <hr className="separator" />
              </>
            ) : (
              <Typography className="mobile-payment-header-title" variant="h3">
                {MOBILE_HEADING_TITLE_TEXT}
              </Typography>
            )}

            <Box className="mobile-payment-header-subtitle-wrapper">
              <Typography
                className={clsx("mobile-payment-header-subtitle", {
                  vesper: isVesper,
                })}
                variant="body2"
              >
                {MOBILE_HEADING_SUBTITLE_TEXT}
              </Typography>
              <Tooltip
                content={
                  <Typography className="payment-earn-info-popover-content">
                    {PAYMENT_EARN_INFO_TOOLTIP_TEXT}
                  </Typography>
                }
                triggerClassName="payment-earn-info-btn"
                title="Rewards & Payment"
              >
                <Icon name={IconName.InfoCircle} />
              </Tooltip>
            </Box>
          </Box>
          {isPackageCheckoutOptimizationExperiment ? undefined : (
            <hr className="heading-rule" />
          )}
          {renderContent}
          {isPackageCheckoutOptimizationExperiment ? (
            <PackagesPriceBreakdown isMobile />
          ) : undefined}
          {isCostCoveredBySelectedPaymentMethods && (
            <MobileFloatingButton
              className="packages-book-mobile-review-trip-button"
              onClick={handleContinue}
              wrapperClassName="b2b"
            >
              {isPackageCheckoutOptimizationExperiment
                ? REVIEW_PACKAGE_CTA_TEXT
                : REVIEW_TRIP_CTA_TEXT}
            </MobileFloatingButton>
          )}
        </MobilePopoverCard>
      ) : (
        <>
          {renderContent}
          <ActionButton
            onClick={handleContinue}
            message={CONFIRM_AND_BOOK}
            className="packages-continue-button book"
          />
        </>
      )}
      <GenericInfoPopup
        open={errorModalOpen}
        image={
          <Icon
            className="error-icon"
            name={isCorporate ? IconName.Caution : IconName.ErrorState}
          />
        }
        zIndex={1301}
        {...errorModalProps}
      />
      <LoadingPopup
        classes={[
          clsx("packages-book-validating-card-modal", {
            mobile: matchesMobile,
          }),
        ]}
        indicatorSize="small"
        indicator={B2BSpinner}
        open={nestedChildState === PaymentInformationChildState.verify}
        popupSize="small"
        message={CARD_VERIFY_TEXT}
        footer={PoweredByHopper}
        textAlign="center"
        fullScreen={isMobile}
        verticalAlignment={isMobile ? "center" : undefined}
      />
    </>
  );
};
